import { Button, Col, Form, Input, Row, Select, Switch } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FormProps } from 'antd/es/form/Form';

// services
import NotificationService from 'core/services/notification';
import { onGetAllCountries } from 'modules/countries/services/countries';
import { CreateRegionPayload, onGetRegion, onUpdateRegion } from 'modules/regions/services/regions';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	country_id: number;
	active: 0 | 1;
};

function UpdateRegionPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { id } = useParams();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [countries, setCountries] = useState<{ label: string; value: number }[]>([]);
	const [updateForm] = Form.useForm();

	useEffect(() => {
		getRegion();
	}, []);

	const getRegion = async () => {
		try {
			const regionResponse = await onGetRegion(id as string);
			const region = regionResponse.data.data;

			const countriesResponse = await onGetAllCountries();
			setCountries(
				countriesResponse.data.data.countries.map((el: any) => {
					return { label: el.name, value: el.id };
				}),
			);

			updateForm.setFieldsValue({ 'name[ar]': region.name.ar });
			updateForm.setFieldsValue({ 'name[en]': region.name.en });
			updateForm.setFieldsValue({ country_id: region.country.id });
			updateForm.setFieldsValue({ active: region.active });

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('REGIONS.FETCH_ERROR'));
			navigate('/countries');
		}
	};

	const onStatusChange = async (checked: boolean) => {
		updateForm.setFieldValue('active', checked ? 1 : 0);
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const updateRegionPayload: Partial<CreateRegionPayload> = {
				name: {
					ar: values['name[ar]'],
					en: values['name[en]'],
				},
				active: values.active,
				country_id: values.country_id,
			};

			await onUpdateRegion(id as string, updateRegionPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('REGIONS.UPDATED_SUCCESS'));
			navigate('/regions');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('REGIONS.UPDATE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('REGIONS.UPDATE_TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className="sectionContainer">
					<h5 style={{ marginBottom: 20 }}>{t('REGIONS.DETAILS')}</h5>

					<Form name="updateForm" form={updateForm} onFinish={onSubmit}>
						<h6>{t('REGIONS.INFORMATION')}</h6>
						<Row gutter={[20, 20]}>
							<Col span={24} md={12}>
								<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('REGIONS.VALIDATION.NAME_EN') }]}>
									<Input size="large" placeholder={t('REGIONS.PLACEHOLDER.NAME_EN')} />
								</Form.Item>
							</Col>
							<Col span={24} md={12}>
								<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('REGIONS.VALIDATION.NAME_AR') }]}>
									<Input size="large" placeholder={t('REGIONS.PLACEHOLDER.NAME_AR')} />
								</Form.Item>
							</Col>
						</Row>

						<h6>{t('REGIONS.COUNTRY')}</h6>
						<Row gutter={[20, 20]}>
							<Col span={24} md={12}>
								<Form.Item<FieldType> name="country_id" rules={[{ required: true, message: t('REGIONS.VALIDATION.COUNTRY') }]}>
									<Select
										style={{ width: '100%' }}
										size="large"
										options={countries}
										loading={isLoading}
										showSearch
										filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
									/>
								</Form.Item>
							</Col>
						</Row>

						<Row>
							<Col span={24} md={12}>
								<div className="d-flex gap-2 mb-3">
									<Form.Item<FieldType> name="active" hidden>
										<Input />
									</Form.Item>
									<Switch defaultChecked={updateForm.getFieldValue('active')} onChange={onStatusChange} />
									<p className="mb-0 fw-medium">{t('COMMON.STATUS')}</p>
								</div>
							</Col>
						</Row>
					</Form>

					<Form.Item>
						<Button
							size="middle"
							type="primary"
							htmlType="submit"
							form="updateForm"
							loading={isSubmitting}
							style={{ marginTop: 20, display: 'flex' }}>
							{t('COMMON.UPDATE')}
						</Button>
					</Form.Item>
				</div>
			)}
		</>
	);
}

export default UpdateRegionPage;
